import { Amplify } from 'aws-amplify';
import { PropsWithChildren, useEffect, useRef, useState, createContext, useContext } from 'react';
import { env } from '../lib/env';

interface CognitoContextValue {
  isConfigured: boolean;
}

const CognitoContext = createContext<CognitoContextValue>({ isConfigured: false });

export const useCognitoContext = () => useContext(CognitoContext);

export const CognitoProvider = ({ children }: PropsWithChildren) => {
  const userPoolId = env.VITE_APP_COGNITO_USER_POOL_ID;
  const clientId = env.VITE_APP_COGNITO_CLIENT_ID;
  const region = env.VITE_APP_COGNITO_REGION;
  const cognitoDomain = env.VITE_APP_COGNITO_DOMAIN;
  const hasConfigured = useRef(false);
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    if (!(userPoolId && clientId)) {
      console.error(
        'Cognito configuration is missing. Please set VITE_APP_COGNITO_USER_POOL_ID and VITE_APP_COGNITO_CLIENT_ID',
      );
      return;
    }

    // Prevent reconfiguration if already configured
    if (hasConfigured.current) {
      setIsConfigured(true);
      return;
    }

    // Get the current origin for redirect URLs
    const redirectSignIn = window.location.origin;
    const redirectSignOut = window.location.origin;

    // Construct Cognito domain if not provided
    // Format: {domain-prefix}.auth.{region}.amazoncognito.com
    const domain = cognitoDomain
      ? cognitoDomain
      : `${env.VITE_APP_BUILD_ENV === 'prod' ? 'prod' : 'dev'}-margembie-labradors.auth.${region}.amazoncognito.com`;

    // Configure Amplify with Cognito OAuth
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId,
          userPoolClientId: clientId,
          loginWith: {
            oauth: {
              domain,
              scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
              redirectSignIn: [redirectSignIn],
              redirectSignOut: [redirectSignOut],
              responseType: 'code',
            },
          },
        },
      },
    });

    hasConfigured.current = true;
    setIsConfigured(true);
  }, [userPoolId, clientId, region, cognitoDomain]);

  if (!(userPoolId && clientId)) {
    return null;
  }

  return <CognitoContext.Provider value={{ isConfigured }}>{children}</CognitoContext.Provider>;
};
